import {
  TOP_NAV_DATA_GA,
  TOP_NAV_DATA_TEST,
} from '@/app/components/layouts/top-navigation/tracking';
import { MENU_LINK_CV, MENU_LINK_LCV } from '@/app/contents/top-navigation';
import type { MenuLinkType } from '@/app/types/top-navigation';
import type { VehicleType } from '@/app/types/vehicle-type';
import { createDataGA } from '@/app/utils/createDataGA';
import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { CSS } from '@/stitches.config';

interface ItemLinkProps {
  isActiveVehicle?: boolean;
  href: string;
  label?: string;
  target?: HTMLAnchorElement['target'];
}

const ItemLink = ({
  href,
  label = '',
  target = '_self',
  isActiveVehicle,
}: ItemLinkProps) => {
  const baseCss: CSS = {
    display: 'flex',
    alignItems: 'center',
    height: '$size-full',
    '@hover': {
      '&:hover': {
        color: '#afafaf', // tailwind gray-300
      },
    },
    '&:focus-visible': {
      color: '#afafaf', // tailwind gray-300
    },
  };
  const activeCss: CSS = {
    position: 'relative',
    justifyContent: 'center',
    pe: 'none',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      borderTop: 'none',
      borderLeft: '6px solid transparent',
      borderRight: '6px solid transparent',
      borderBottom: '6px solid $white',
      pointerEvents: 'none',
    },
  };

  if (!label || !href) return null;

  return (
    <Link
      href={isActiveVehicle ? '/' : href}
      target={isActiveVehicle ? '_self' : target}
      role="menuitem"
      aria-label={label}
      test={{
        dataTest: TOP_NAV_DATA_TEST.navigationMenuLink,
      }}
      tracking={{
        dataGA: createDataGA({
          click: true,
          eventName: TOP_NAV_DATA_GA.buyingTools,
          url: isActiveVehicle ? '/' : href,
          text: label,
        }),
      }}
      css={{
        ...baseCss,
        ...(isActiveVehicle ? activeCss : {}),
      }}
    >
      <Text
        size="text-base-2"
        font={isActiveVehicle ? 'bold' : 'base'}
        variant="text-current"
      >
        {label}
      </Text>
    </Link>
  );
};

interface BuyingToolsProps {
  vehicleType: VehicleType;
  items: MenuLinkType[];
}

export const BuyingTools = ({ vehicleType, items }: BuyingToolsProps) => {
  return (
    <Box
      component="aside"
      position="relative"
      zIndex="floating"
      display={{ '@initial': 'none', '@xl': 'block' }}
      css={{
        backgroundColor: '#3b3b3b', // tailwind gray-800
        color: '$white',
        zIndex: 1003, // Note: On top of zIndex: $header
      }}
      test={{
        dataTest: TOP_NAV_DATA_TEST.buyingToolsNavigation,
      }}
    >
      <Container
        data-test="buying-tools-menu"
        aria-label="Buying tools navigation"
        size="lg"
        as="nav"
        role="menubar"
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          height: '$size-10', // Header BuyingTools Height
        }}
      >
        <Box display="flex" alignItems="center" gap="4">
          <ItemLink
            href={MENU_LINK_LCV.href}
            label={MENU_LINK_LCV.label}
            target={MENU_LINK_LCV.target}
            isActiveVehicle={vehicleType === 'lcv'}
          />
          <ItemLink
            href={MENU_LINK_CV.href}
            label={MENU_LINK_CV.label}
            target={MENU_LINK_CV.target}
            isActiveVehicle={vehicleType === 'cv'}
          />
        </Box>
        <Box display="flex" alignItems="center" gap="4">
          {items.map((item, index) => (
            <ItemLink
              key={`buying_nav_${index}`}
              href={item.href}
              label={item.label}
              target={item.target}
            />
          ))}
        </Box>
      </Container>
    </Box>
  );
};
